<script>
export default {
  name: "CardResult",
  props: {
    validationResult: {
      type: Object,
      default: null,
    },
    errorCode: {
      type: String,
      default: null,
    },
  },
  computed: {
    validationStatus() {
      if (!this.validationResult) return null;

      return {
        token: this.validationResult.isTokenValid,
        patient: this.validationResult.isPatientActive,
        service: this.validationResult.isMedicalServiceValid,
      };
    },

    shouldShow() {
      return this.hasValidationResult || this.hasError;
    },

    hasValidationResult() {
      return this.validationResult !== null;
    },

    hasError() {
      return this.errorCode && this.errorCode.trim() !== "";
    },

    alertConfig() {
      if (!this.validationStatus) return {};

      const isValid = this.validationStatus.token;
      return {
        title: isValid ? "Válido" : "Inválido",
        type: isValid ? "success" : "error",
      };
    },
  },

  methods: {
    getStatusIcon(isValid) {
      return {
        name: isValid ? "check_circle_outline" : "cancel_circle_outline",
        color: isValid ? "#67c23a" : "#f56c6c",
      };
    },

    getStatusText(isValid, isService = false) {
      const baseText = isValid ? "Válido" : "Inválido";
      return isService ? baseText.replace("o", "a") : baseText;
    },
  },
};
</script>

<template lang="pug">
div.card-result-container(v-if="shouldShow")
  div(v-if="hasValidationResult")
    el-alert.validation-result(
      :title="alertConfig.title"
      :type="alertConfig.type"
      :closable="false"
    )
      div.result-content
        div.result-item
          span.result-label Token
          div.result-item-content
            micon(
              :name="getStatusIcon(validationStatus.token).name"
              :color="getStatusIcon(validationStatus.token).color"
              width="18"
              height="18"
            )
            span.result-value(:class="{ 'valid': validationStatus.token, 'invalid': !validationStatus.token }")
              | {{ getStatusText(validationStatus.token) }}
        
        div.result-divider
        
        div.result-item
          span.result-label Estado del paciente
          div.result-item-content
            micon(
              :name="getStatusIcon(validationStatus.patient).name"
              :color="getStatusIcon(validationStatus.patient).color"
              width="18"
              height="18"
            )
            span.result-value(:class="{ 'valid': validationStatus.patient, 'invalid': !validationStatus.patient }")
              | {{ getStatusText(validationStatus.patient) }}
        
        div.result-divider
        
        div.result-item
          span.result-label Prestación
          div.result-item-content
            micon(
              :name="getStatusIcon(validationStatus.service).name"
              :color="getStatusIcon(validationStatus.service).color"
              width="18"
              height="18"
            )
            span.result-value(:class="{ 'valid': validationStatus.service, 'invalid': !validationStatus.service }")
              | {{ getStatusText(validationStatus.service, true) }}
  
  div(v-if="hasError")
    el-alert.error-alert(
      :title="errorCode"
      type="error"
      :closable="false"
    )
      p.error-message {{ errorCode }}
</template>

<style lang="scss" scoped>
.card-result-container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;

  .validation-result {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .result-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 8px;

      .result-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 0;
        font-size: 14px;
        line-height: 1.4;

        .result-label {
          font-weight: 600;
          min-width: 140px;
          margin-right: 8px;
        }

        .result-item-content {
          display: flex;
          align-items: center;
          gap: 5px;

          .result-value {
            font-weight: bold;
            font-size: 14px;

            &.valid {
              color: #67c23a;
            }

            &.invalid {
              color: #f56c6c;
            }
          }
        }
      }

      .result-divider {
        height: 1px;
        background: linear-gradient(
          to right,
          transparent,
          #bec2c9,
          transparent
        );
        margin: 8px 0;
        border-radius: 1px;
      }
    }
  }

  .error-alert {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .error-message {
      margin: 0;
      font-size: 14px;
      color: #f56c6c;
    }
  }
}

@media (max-width: 768px) {
  .result-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .result-label {
    min-width: auto;
    margin-right: 0;
  }

  .result-value {
    align-self: flex-start;
  }
}
</style>
